import React, { useState, useMemo } from 'react';
import { useMaerkeDage } from '../hooks/useMaerkeDage';
import { useFoedselsDage } from '../hooks/useFoedselsDage';
import { resolverAlleOfficielle, resolverBrugerBegivenheder } from '../utils/dateUtils';
import { KalenderBegivenhed } from '../types';
import CalendarGrid from '../components/calendar/CalendarGrid';
import CalendarHeader from '../components/calendar/CalendarHeader';
import DayDetailDrawer from '../components/calendar/DayDetailDrawer';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import styles from './Kalender.module.scss';

const CalendarLegend: React.FC = () => (
  <div className={styles.legend}>
    <div className={styles.legendItem}>
      <span className={`${styles.legendDot} ${styles.dotOfficiel}`} />
      <span>Officiel flagdag</span>
    </div>
    <div className={styles.legendItem}>
      <span className={`${styles.legendDot} ${styles.dotFoedselsdag}`} />
      <span>Fødselsdag</span>
    </div>
    <div className={styles.legendItem}>
      <span className={`${styles.legendDot} ${styles.dotMaerkedag}`} />
      <span>Mærkedag</span>
    </div>
  </div>
);

const Kalender: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [valgtDag, setValgtDag] = useState<Date | null>(null);

  const { maerkeDage, loading: loadingMD } = useMaerkeDage();
  const { foedselsDage, loading: loadingFD } = useFoedselsDage();

  const loading = loadingMD || loadingFD;

  const begivenheder = useMemo((): KalenderBegivenhed[] => {
    const officielle = resolverAlleOfficielle(year);
    const bruger = resolverBrugerBegivenheder(maerkeDage, foedselsDage, year);
    return [...officielle, ...bruger];
  }, [maerkeDage, foedselsDage, year]);

  const handleForrige = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const handleNaeste = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  return (
    <div className={styles.page}>
      <div className={`${styles.content} container`}>
        <h1 className={styles.pageTitle}>Kalender</h1>

        {loading ? (
          <LoadingSpinner centered />
        ) : (
          <div className={styles.calendarCard}>
            <CalendarHeader
              year={year}
              month={month}
              onForrige={handleForrige}
              onNaeste={handleNaeste}
            />
            <CalendarGrid
              year={year}
              month={month}
              begivenheder={begivenheder}
              onDagValgt={setValgtDag}
            />
          </div>
        )}

        <CalendarLegend />
      </div>

      <DayDetailDrawer
        dato={valgtDag}
        begivenheder={begivenheder}
        onLuk={() => setValgtDag(null)}
      />
    </div>
  );
};

export default Kalender;
